import {FeatureFlagKey} from 'common/src/modules/featureFlags/featureFlags.types';
import isFlagEnabled from 'common/src/modules/featureFlags/utils/isFlagEnabled';
import {CLStore} from 'common/src/redux/store';
import {z, ZodTypeAny} from 'zod';
import currentRouteContext from '../../contexts/currentRouteContext/currentRouteContext';
import generalContext from '../../contexts/generalContext';
import {AgentName, LLMResponseType} from '../../copilot.enums';
import {plaintextPartSchema} from '../../copilot.schemas';
import {
  Agent,
  AgentContext,
  AgentScratchpadContext,
  ConversationStarter,
  Tool,
} from '../../copilot.types';
import getDefaultLLMModel from '../../models/utils/getDefaultLLMModel';
import getLLMModel from '../../models/utils/getLLMModel';
import {OpenAIModel} from '../../openAI/openAI.enums';
import {OpenAIModelConfig} from '../../openAI/openAI.models';
import {generalKnowledgeTool} from '../../tools/generalKnowledgeTool';
import resourceKnowledgeTool from '../../tools/resourceKnowledgeTool/resourceKnowledgeTool';
import buildInstructions from '../buildInstructions';
import agentBase from './agentBase';

type CreateAgentParams = {
  name: AgentName;
  role: string;
  instructions?: string;
  inScope: string[];
  outOfScope?: string[];
  contexts?: AgentContext[];
  model?: OpenAIModelConfig;
  classificationModel?: OpenAIModelConfig;
  uiToolNames?: string[];
  widgetSchemas?: ZodTypeAny[];
  tools?: Tool<ZodTypeAny>[];
  store: CLStore;
  conversationStarters?: ConversationStarter[];
  scratchpad?: (chatId: string) => AgentScratchpadContext;
};

export default function agent({
  name,
  role,
  inScope,
  tools = [],
  instructions,
  store,
  conversationStarters = [],
  outOfScope = [],
  uiToolNames = [],
  widgetSchemas,
  contexts = [],
  scratchpad,
  model = getDefaultLLMModel(),
  classificationModel,
}: CreateAgentParams): Agent {
  const isGpt5_2Enabled = isFlagEnabled(FeatureFlagKey.EnableGpt5_2);

  classificationModel ??= isGpt5_2Enabled
    ? getLLMModel(OpenAIModel.Gpt5_2)
    : getLLMModel(OpenAIModel.Gpt5_1);

  const agentTools = [...tools, generalKnowledgeTool()];

  if (isFlagEnabled(FeatureFlagKey.EnableCopilotResourceKnowledge)) {
    agentTools.push(resourceKnowledgeTool());
  }

  const agentContexts = [generalContext, currentRouteContext(store.getState), ...contexts];

  const requiredToolNames = [...(agentTools?.map((tool) => tool.name) ?? []), ...uiToolNames];

  const responseSchema = _buildContentSchema(widgetSchemas as [ZodTypeAny, ...ZodTypeAny[]]);

  const fullInstructions = buildInstructions({
    agent: name,
    role,
    scope: {inScope, outOfScope},
    instructions,
  });

  const agentBaseObject = agentBase({
    name,
    role,
    instructions: fullInstructions,
    tools: agentTools,
    model,
    classificationModel,
    responseSchema,
    scratchpad,
  });

  return {
    ...agentBaseObject,
    name,
    scope: {inScope, outOfScope},
    requiredToolNames,
    contexts: agentContexts,
    scratchpad,
    store,
    conversationStarters,
  };
}

function _buildContentSchema(
  additionalContentPartSchemas?: [ZodTypeAny, ...ZodTypeAny[]],
): ZodTypeAny {
  const partsSchema = additionalContentPartSchemas
    ? z.union([plaintextPartSchema, ...additionalContentPartSchemas])
    : plaintextPartSchema;

  return z.object({
    type: z
      .literal(LLMResponseType.Content)
      .describe(`A '${LLMResponseType.Content}' type of response`),
    parts: z.array(partsSchema),
  });
}
